import { BoundMultiSelectField, BoundSelectField, useComputed } from "@homebound/beam";
import { ObjectState } from "@homebound/form-state";
import { Observer } from "mobx-react";
import { useEffect } from "react";
import { NamedFragment } from "src/fragments/commonFragments.generated";
import { ProjectTypeFilter } from "src/generated/graphqlTypes";
import { AddBidPackagePage_OriginatingProjectFragment } from "src/routes/bids/add-bid-package/AddBidPackagePage.generated";
import { useBidProjectFieldsQuery } from "src/routes/bids/add-bid-package/components/BidProjectFields.generated";
import { EditBidPackageFormState } from "../../components/EditBidPackageModal";
import { BidPackageFormState } from "../AddBidPackagePage";

type BidProjectFieldsProps = {
  formState: ObjectState<BidPackageFormState> | ObjectState<EditBidPackageFormState>;
  markets: NamedFragment[];
  disabled?: boolean | { market: boolean; projects: boolean };
  originatingProject?: AddBidPackagePage_OriginatingProjectFragment;
};

export function BidProjectFields({ formState, markets, disabled = false, originatingProject }: BidProjectFieldsProps) {
  const marketId = useComputed(() => formState.marketId.value, [formState.marketId.value]);
  const { data } = useBidProjectFieldsQuery({
    variables: { projectFilter: { market: [marketId!], type: [ProjectTypeFilter.Community] } },
    skip: !marketId,
  });
  const projects = data?.projects;

  // If we are creating a bid package from a community, prefill it as the selected community
  useEffect(() => {
    if (originatingProject && projects?.nonEmpty && formState.projects.value === undefined) {
      const p = projects.find((p) => p.id === originatingProject.id);
      formState.projects.set(p ? [p.id] : []);
    }
  }, [projects, originatingProject, formState]);

  return (
    <Observer>
      {() => (
        <>
          {!originatingProject && (
            <BoundSelectField
              required
              options={markets}
              field={formState.marketId}
              onSelect={(option) => {
                // Clear out projects when the market changes
                formState.projects.set([]);
                formState.marketId.set(option);
              }}
              disabled={disabled instanceof Object ? disabled.market : disabled}
            />
          )}
          {formState.marketId.value && (
            <BoundMultiSelectField
              label="Communities"
              options={projects ?? []}
              field={formState.projects}
              nothingSelectedText="All"
              disabled={disabled instanceof Object ? disabled.projects : disabled}
            />
          )}
        </>
      )}
    </Observer>
  );
}
